/*
 * The document head of the current page. The brand gives the defaults (HealthGate), pages add
 * their own through usePageMeta, and private paths are always noindex. Everything is recomputed
 * from those three on every change, so the order pages mount and unmount in does not matter.
 * apps/main/backend/src/modules/seo writes the same head for crawlers: keep the two in step.
 */
import { getMediaUrl } from "@/configs";
import { LANG_CODES, matchLanguage } from "@/locales/configLang";

export interface PageMeta {
  /** The page's own title; shown as "<title> | <site name>". */
  title?: string;
  description?: string;
  /** Path for the canonical URL; defaults to the current path without its query. */
  canonicalPath?: string;
  noindex?: boolean;
  type?: "website" | "article";
  /** The page's own link-preview image (a game's artwork); else the brand's. */
  image?: string;
  /** Structured data, one JSON-LD script each. */
  jsonLd?: object[];
}

interface SiteMeta {
  name: string;
  headText: string;
  description: string;
  /** The brand's link-preview image and logo, as stored; empty = the public/ defaults. */
  image: string;
  logo: string;
}

// Private or per-player pages; apps/main/backend/src/modules/seo/datas/routes.ts has the same list.
const NOINDEX_PREFIXES = [
  "/settings",
  "/transactions",
  "/notifications",
  "/recent",
  "/favorites",
  "/casino/my-bets",
  "/sports/my-bets",
  "/game-room",
  "/referral",
  "/rewards",
  "/shop",
  "/share",
  "/verify-email",
  "/confirm-email-change",
  "/unsubscribe",
  "/search",
  "/loading",
  "/icons",
];

// og:locale per language; same table as SEO_VARIANTS in the backend.
const OG_LOCALES: Record<string, string> = {
  en: "en_US",
  es: "es_ES",
  "es-AR": "es_AR",
  it: "it_IT",
  pt: "pt_BR",
};

export const isNoindexPath = (path: string): boolean =>
  NOINDEX_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

let site: SiteMeta | null = null;
const stack: { id: number; meta: PageMeta }[] = [];
let nextId = 1;

const MARK = "data-page-meta";

const setTag = (selector: string, create: () => HTMLElement, attr: string, value?: string) => {
  let element = document.head.querySelector<HTMLElement>(selector);
  if (value === undefined) {
    element?.remove();
    return;
  }
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.setAttribute(attr, value);
};

const meta = (key: "name" | "property", value: string) => () => {
  const element = document.createElement("meta");
  element.setAttribute(key, value);
  return element;
};

const canonicalLink = () => {
  const element = document.createElement("link");
  element.rel = "canonical";
  return element;
};

const addMarked = (tag: "link" | "meta" | "script", attrs: Record<string, string>, text = "") => {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, value);
  element.setAttribute(MARK, "");
  if (text) element.textContent = text;
  document.head.appendChild(element);
};

// An uploaded image (media path or URL) as an absolute URL, as og:image needs.
const absolute = (value: string, origin: string): string => {
  const url = getMediaUrl(value);
  return url.startsWith("/") && !url.startsWith("//") ? `${origin}${url}` : url;
};

// "/" redirects to the lobby, which is the front page.
const FRONT_PAGE = "/casino/lobby";

// The site itself, on its front page only.
const siteJsonLd = (current: SiteMeta, origin: string): object[] => [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: current.name,
    url: `${origin}/`,
    logo: absolute(current.logo, origin) || `${origin}/logo-512.png`,
  },
  { "@context": "https://schema.org", "@type": "WebSite", name: current.name, url: `${origin}/` },
];

/** Writes the merged head. Later entries win per field; noindex from any entry or the path wins. */
export const refreshPageMeta = () => {
  if (typeof document === "undefined" || !site) return;
  const merged: PageMeta = {};
  for (const entry of stack) {
    for (const [key, value] of Object.entries(entry.meta)) {
      if (value !== undefined) (merged as Record<string, unknown>)[key] = value;
    }
  }
  const { origin, pathname: path, search } = window.location;
  const noindex = isNoindexPath(path) || stack.some((entry) => entry.meta.noindex);
  const title = merged.title ? `${merged.title} | ${site.name}` : site.headText || site.name;
  const description = merged.description || site.description;
  const canonicalPath = merged.canonicalPath ?? path;
  // A URL that names its language is its own canonical, as the hreflang links say; the bare URL is x-default.
  const namedLang = matchLanguage(new URLSearchParams(search).get("lang"));
  const bare = `${origin}${canonicalPath}`;
  const url = namedLang ? `${bare}?lang=${encodeURIComponent(namedLang)}` : bare;
  const lang = matchLanguage(document.documentElement.lang) ?? LANG_CODES[0];
  const ownImage = absolute(merged.image ?? "", origin) || absolute(site.image, origin);
  const image = ownImage || `${origin}/og-image.png`;

  document.title = title;
  setTag("meta[name='description']", meta("name", "description"), "content", description);
  setTag("link[rel='canonical']", canonicalLink, "href", url);
  setTag("meta[property='og:site_name']", meta("property", "og:site_name"), "content", site.name);
  setTag(
    "meta[property='og:type']",
    meta("property", "og:type"),
    "content",
    merged.type ?? "website",
  );
  setTag("meta[property='og:title']", meta("property", "og:title"), "content", title);
  setTag(
    "meta[property='og:description']",
    meta("property", "og:description"),
    "content",
    description,
  );
  setTag("meta[property='og:url']", meta("property", "og:url"), "content", url);
  setTag("meta[property='og:image']", meta("property", "og:image"), "content", image);
  // Only the default image's size is known.
  setTag(
    "meta[property='og:image:width']",
    meta("property", "og:image:width"),
    "content",
    ownImage ? undefined : "1200",
  );
  setTag(
    "meta[property='og:image:height']",
    meta("property", "og:image:height"),
    "content",
    ownImage ? undefined : "630",
  );
  setTag("meta[property='og:image:alt']", meta("property", "og:image:alt"), "content", site.name);
  setTag("meta[property='og:locale']", meta("property", "og:locale"), "content", OG_LOCALES[lang]);
  setTag("meta[name='twitter:title']", meta("name", "twitter:title"), "content", title);
  setTag(
    "meta[name='twitter:description']",
    meta("name", "twitter:description"),
    "content",
    description,
  );
  setTag("meta[name='twitter:image']", meta("name", "twitter:image"), "content", image);
  setTag(
    "meta[name='robots']",
    meta("name", "robots"),
    "content",
    noindex ? "noindex, nofollow" : undefined,
  );

  // The crawler head's copies (unmarked) go too, so each tag exists once.
  document.head
    .querySelectorAll(
      `[${MARK}], link[rel='alternate'][hreflang], meta[property='og:locale:alternate'], script[type='application/ld+json']`,
    )
    .forEach((element) => element.remove());
  if (!noindex) {
    for (const code of LANG_CODES) {
      addMarked("link", { rel: "alternate", hreflang: code, href: `${bare}?lang=${code}` });
    }
    addMarked("link", { rel: "alternate", hreflang: "x-default", href: bare });
  }
  for (const code of LANG_CODES) {
    if (code !== lang)
      addMarked("meta", { property: "og:locale:alternate", content: OG_LOCALES[code] });
  }
  const jsonLd = [
    ...(canonicalPath === FRONT_PAGE ? siteJsonLd(site, origin) : []),
    ...(merged.jsonLd ?? []),
  ];
  for (const item of jsonLd) {
    addMarked("script", { type: "application/ld+json" }, JSON.stringify(item));
  }
};

/** The brand's defaults, from the boot config. */
export const setSiteMeta = (next: {
  name?: string;
  headText?: string;
  desc?: string;
  ogImage?: string;
  darkLogo?: string;
  lightLogo?: string;
}) => {
  const name = (next.name || "").trim() || document.title;
  site = {
    name,
    headText: (next.headText || "").trim() || name,
    description: next.desc ?? "",
    image: next.ogImage ?? "",
    logo: next.darkLogo || next.lightLogo || "",
  };
  refreshPageMeta();
};

export const pushPageMeta = (value: PageMeta): number => {
  const id = nextId++;
  stack.push({ id, meta: value });
  refreshPageMeta();
  return id;
};

export const updatePageMeta = (id: number, value: PageMeta) => {
  const entry = stack.find((item) => item.id === id);
  if (entry) entry.meta = value;
  refreshPageMeta();
};

export const removePageMeta = (id: number) => {
  const index = stack.findIndex((item) => item.id === id);
  if (index >= 0) stack.splice(index, 1);
  refreshPageMeta();
};
